import React from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  useApi,
  fetchApiRef,
  configApiRef,
} from '@backstage/core-plugin-api';
import { EmptyState } from '@backstage/core-components';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  Collapse,
  TextField,
  MenuItem,
  CircularProgress,
  Button,
  ButtonGroup,
  Switch,
  FormControlLabel,
  LinearProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import RefreshIcon from '@material-ui/icons/Refresh';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import CancelIcon from '@material-ui/icons/Cancel';
import ScheduleIcon from '@material-ui/icons/Schedule';
import SkipNextIcon from '@material-ui/icons/SkipNext';
import BuildIcon from '@material-ui/icons/Build';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import ReplayIcon from '@material-ui/icons/Replay';
import MergeTypeIcon from '@material-ui/icons/MergeType';
import CallSplitIcon from '@material-ui/icons/CallSplit';
import StopIcon from '@material-ui/icons/Stop';

/* ─── Types ─── */

interface Pipeline {
  id: number;
  iid: number;
  status: string;
  ref: string;
  sha: string;
  created_at: string;
  updated_at: string;
  web_url: string;
  source: string;
}

interface Job {
  id: number;
  name: string;
  status: string;
  stage: string;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  duration: number | null;
  web_url: string;
  allow_failure: boolean;
  pipeline: { id: number };
}

interface MergeRequest {
  id: number;
  iid: number;
  title: string;
  state: string;
  source_branch: string;
  target_branch: string;
  author: { name: string; username: string; avatar_url: string };
  created_at: string;
  updated_at: string;
  merged_at: string | null;
  closed_at: string | null;
  web_url: string;
  merge_status: string;
  has_conflicts: boolean;
  draft: boolean;
  pipeline?: { id: number; status: string };
}

/* ─── Styles ─── */

const useStyles = makeStyles(theme => ({
  toolbar: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: theme.spacing(1),
  },
  pipelineRow: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 1.5),
    cursor: 'pointer',
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  pipelineId: {
    fontFamily: 'monospace',
    fontSize: '0.85rem',
    fontWeight: 600,
    minWidth: 70,
    color: theme.palette.primary.main,
  },
  pipelineRef: {
    fontFamily: 'monospace',
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    maxWidth: 120,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  pipelineSha: {
    fontFamily: 'monospace',
    fontSize: '0.75rem',
    color: theme.palette.text.hint,
    minWidth: 70,
  },
  pipelineTime: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    minWidth: 130,
    textAlign: 'right' as const,
  },
  stageRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5, 0),
  },
  stageLabel: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase' as const,
    minWidth: 100,
  },
  jobChip: {
    cursor: 'pointer',
    margin: theme.spacing(0.25),
  },
  jobsPanel: {
    padding: theme.spacing(1, 2, 2, 2),
    backgroundColor: theme.palette.type === 'dark' ? '#1a1a2e' : '#fafafa',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  logContainer: {
    backgroundColor: '#1e1e1e',
    color: '#d4d4d4',
    fontFamily: '"JetBrains Mono", "Fira Code", "Cascadia Code", Consolas, monospace',
    fontSize: '11px',
    lineHeight: '1.4',
    padding: theme.spacing(1),
    borderRadius: 4,
    maxHeight: 400,
    minHeight: 100,
    overflowY: 'auto',
    overflowX: 'auto',
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-all' as const,
    marginTop: theme.spacing(1),
  },
  jobHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0.5, 0),
    marginBottom: theme.spacing(0.5),
  },
  statusBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(0.5, 1),
    backgroundColor: theme.palette.type === 'dark' ? '#252526' : '#f3f3f3',
    borderRadius: '0 0 4px 4px',
    fontSize: 12,
  },
  mrTable: {
    '& th': {
      fontWeight: 600,
      fontSize: '0.8rem',
      padding: theme.spacing(1, 1.5),
      whiteSpace: 'nowrap' as const,
    },
    '& td': {
      fontSize: '0.8rem',
      padding: theme.spacing(0.75, 1.5),
    },
  },
  mrTitle: {
    fontWeight: 500,
    maxWidth: 300,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  branchChip: {
    fontFamily: 'monospace',
    fontSize: '0.7rem',
    height: 22,
  },
}));

/* ─── Constants ─── */

const PROXY_BASE = '/api/proxy/gitlab/api/v4';

/* ─── Helpers ─── */

function statusIcon(status: string, size: 'small' | 'inherit' = 'small') {
  const fontSize = size === 'small' ? 16 : 20;
  switch (status) {
    case 'success':
      return <CheckCircleIcon style={{ fontSize, color: '#4caf50' }} />;
    case 'failed':
      return <ErrorIcon style={{ fontSize, color: '#f44336' }} />;
    case 'running':
      return <PlayArrowIcon style={{ fontSize, color: '#2196f3' }} />;
    case 'pending':
    case 'waiting_for_resource':
      return <ScheduleIcon style={{ fontSize, color: '#ff9800' }} />;
    case 'canceled':
      return <CancelIcon style={{ fontSize, color: '#757575' }} />;
    case 'skipped':
      return <SkipNextIcon style={{ fontSize, color: '#9e9e9e' }} />;
    case 'manual':
      return <BuildIcon style={{ fontSize, color: '#9e9e9e' }} />;
    case 'created':
      return <FiberManualRecordIcon style={{ fontSize, color: '#bdbdbd' }} />;
    default:
      return <FiberManualRecordIcon style={{ fontSize, color: '#bdbdbd' }} />;
  }
}

function statusColor(status: string): string {
  switch (status) {
    case 'success': return '#4caf50';
    case 'failed': return '#f44336';
    case 'running': return '#2196f3';
    case 'pending': case 'waiting_for_resource': return '#ff9800';
    case 'canceled': return '#757575';
    case 'skipped': return '#9e9e9e';
    case 'manual': return '#9e9e9e';
    default: return '#bdbdbd';
  }
}

function statusLabel(status: string): string {
  switch (status) {
    case 'success': return '성공';
    case 'failed': return '실패';
    case 'running': return '실행 중';
    case 'pending': return '대기';
    case 'canceled': return '취소';
    case 'skipped': return '스킵';
    case 'manual': return '수동';
    case 'created': return '생성됨';
    case 'waiting_for_resource': return '리소스 대기';
    default: return status;
  }
}

function mrStateLabel(state: string): string {
  switch (state) {
    case 'opened': return 'Open';
    case 'merged': return 'Merged';
    case 'closed': return 'Closed';
    default: return state;
  }
}

function mrStateColor(state: string): string {
  switch (state) {
    case 'opened': return '#2196f3';
    case 'merged': return '#4caf50';
    case 'closed': return '#f44336';
    default: return '#9e9e9e';
  }
}

function formatDuration(seconds: number | null): string {
  if (!seconds) return '-';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function formatTime(isoStr: string): string {
  if (!isoStr) return '-';
  const d = new Date(isoStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return '방금';
  if (diffMin < 60) return `${diffMin}분 전`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour}시간 전`;
  const diffDay = Math.floor(diffHour / 24);
  if (diffDay < 7) return `${diffDay}일 전`;
  return d.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
}

/** Extract pipeline iid from web_url as fallback (e.g. .../pipelines/123 -> 123) */
function extractIidFromUrl(webUrl: string): number | null {
  const m = webUrl?.match(/\/pipelines\/(\d+)/);
  return m ? Number(m[1]) : null;
}

/** Resolve display number: prefer iid, fallback to web_url extraction, then id */
function pipelineDisplayId(p: Pipeline): number {
  if (p.iid) return p.iid;
  const fromUrl = extractIidFromUrl(p.web_url);
  return fromUrl ?? p.id;
}

/** Strip ANSI escape codes from job trace output */
function stripAnsi(text: string): string {
  // eslint-disable-next-line no-control-regex
  return text.replace(/\x1b\[[0-9;]*[a-zA-Z]/g, '')
    .replace(/section_start:\d+:[^\r\n]*\r?/g, '')
    .replace(/section_end:\d+:[^\r\n]*\r?/g, '');
}

/* ─── Sub-components ─── */

const JobLogViewer: React.FC<{
  projectId: number;
  jobId: number;
  jobName: string;
  jobStatus: string;
  jobWebUrl: string;
  gitlabGetText: (path: string) => Promise<string>;
}> = ({ projectId, jobId, jobName, jobStatus, jobWebUrl, gitlabGetText }) => {
  const classes = useStyles();
  const [log, setLog] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const logRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const text = await gitlabGetText(
          `/projects/${projectId}/jobs/${jobId}/trace`,
        );
        if (!cancelled) setLog(stripAnsi(text));
      } catch (e: unknown) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId, jobId, gitlabGetText]);

  React.useEffect(() => {
    if (logRef.current && jobStatus === 'running') {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [log, jobStatus]);

  return (
    <Box>
      <Box className={classes.jobHeader}>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          {statusIcon(jobStatus)}
          <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
            {jobName}
          </Typography>
          <Chip
            label={statusLabel(jobStatus)}
            size="small"
            style={{
              backgroundColor: statusColor(jobStatus),
              color: '#fff',
              fontWeight: 600,
              height: 20,
            }}
          />
        </Box>
        <Tooltip title="GitLab에서 보기">
          <IconButton
            size="small"
            onClick={() => window.open(jobWebUrl, '_blank')}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {loading && <LinearProgress />}
      {error && (
        <Paper style={{ padding: 8, backgroundColor: '#ffebee' }}>
          <Typography color="error" variant="body2">{error}</Typography>
        </Paper>
      )}

      {log !== null && (
        <div ref={logRef} className={classes.logContainer}>
          {log || '(빈 로그)'}
        </div>
      )}
    </Box>
  );
};

/* ─── Main Component ─── */

type TabView = 'pipelines' | 'merge_requests';

export const GitlabCiCdContent = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const fetchApi = useApi(fetchApiRef);
  const configApi = useApi(configApiRef);

  const slug = entity?.metadata?.annotations?.['gitlab.com/project-slug'] ?? '';
  const gitlabBaseUrl =
    configApi.getOptionalConfigArray('integrations.gitlab')?.[0]
      ?.getOptionalString('baseUrl') ?? '';

  /* ─── State ─── */
  const [tab, setTab] = React.useState<TabView>('pipelines');
  const [projectId, setProjectId] = React.useState<number | null>(null);
  const [pipelines, setPipelines] = React.useState<Pipeline[]>([]);
  const [pipelineJobs, setPipelineJobs] = React.useState<Record<number, Job[]>>({});
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [expandedPipeline, setExpandedPipeline] = React.useState<number | null>(null);
  const [expandedJob, setExpandedJob] = React.useState<number | null>(null);
  const [perPage, setPerPage] = React.useState(10);
  const [refFilter, setRefFilter] = React.useState('');
  const [autoRefresh, setAutoRefresh] = React.useState(false);
  const [actionLoading, setActionLoading] = React.useState<string | null>(null);

  // MR state
  const [mergeRequests, setMergeRequests] = React.useState<MergeRequest[]>([]);
  const [mrLoading, setMrLoading] = React.useState(false);
  const [mrError, setMrError] = React.useState<string | null>(null);
  const [mrStateFilter, setMrStateFilter] = React.useState<string>('opened');
  const [mergeDialog, setMergeDialog] = React.useState<MergeRequest | null>(null);
  const [merging, setMerging] = React.useState(false);

  /* ─── API helpers ─── */
  const resolveProjectId = React.useCallback(async (): Promise<number | null> => {
    if (projectId) return projectId;
    if (!slug) return null;
    const encodedSlug = encodeURIComponent(slug);
    const resp = await fetchApi.fetch(`${PROXY_BASE}/projects/${encodedSlug}`);
    if (!resp.ok) throw new Error(`GitLab API ${resp.status}`);
    const project = await resp.json();
    const pid = project.id as number;
    setProjectId(pid);
    return pid;
  }, [slug, projectId, fetchApi]);

  const gitlabGet = React.useCallback(
    async (path: string) => {
      const resp = await fetchApi.fetch(`${PROXY_BASE}${path}`);
      if (!resp.ok) throw new Error(`GitLab API ${resp.status}`);
      return resp.json();
    },
    [fetchApi],
  );

  const gitlabGetText = React.useCallback(
    async (path: string) => {
      const resp = await fetchApi.fetch(`${PROXY_BASE}${path}`);
      if (!resp.ok) throw new Error(`GitLab API ${resp.status}`);
      return resp.text();
    },
    [fetchApi],
  );

  const gitlabPost = React.useCallback(
    async (path: string) => {
      const resp = await fetchApi.fetch(`${PROXY_BASE}${path}`, { method: 'POST' });
      if (!resp.ok) {
        const body = await resp.text().catch(() => '');
        throw new Error(`GitLab API ${resp.status}: ${body}`);
      }
      return resp.json();
    },
    [fetchApi],
  );

  const gitlabPut = React.useCallback(
    async (path: string) => {
      const resp = await fetchApi.fetch(`${PROXY_BASE}${path}`, { method: 'PUT' });
      if (!resp.ok) {
        const body = await resp.text().catch(() => '');
        throw new Error(`GitLab API ${resp.status}: ${body}`);
      }
      return resp.json();
    },
    [fetchApi],
  );

  /* ─── Fetch pipelines ─── */
  const fetchPipelines = React.useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    try {
      const pid = await resolveProjectId();
      if (!pid) return;

      let url = `/projects/${pid}/pipelines?per_page=${perPage}&order_by=id&sort=desc`;
      if (refFilter) {
        url += `&ref=${encodeURIComponent(refFilter)}`;
      }
      const data = await gitlabGet(url);
      setPipelines(data as Pipeline[]);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [slug, resolveProjectId, perPage, refFilter, gitlabGet]);

  /* ─── Fetch MRs ─── */
  const fetchMergeRequests = React.useCallback(async () => {
    if (!slug) return;
    setMrLoading(true);
    setMrError(null);
    try {
      const pid = await resolveProjectId();
      if (!pid) return;
      const url = `/projects/${pid}/merge_requests?state=${mrStateFilter}&per_page=20&order_by=updated_at&sort=desc`;
      const data = await gitlabGet(url);
      setMergeRequests(data as MergeRequest[]);
    } catch (e: unknown) {
      setMrError(e instanceof Error ? e.message : String(e));
    } finally {
      setMrLoading(false);
    }
  }, [slug, resolveProjectId, mrStateFilter, gitlabGet]);

  React.useEffect(() => {
    if (tab === 'pipelines') fetchPipelines();
  }, [tab, fetchPipelines]);

  React.useEffect(() => {
    if (tab === 'merge_requests') fetchMergeRequests();
  }, [tab, fetchMergeRequests]);

  // Auto-refresh
  React.useEffect(() => {
    if (!autoRefresh) return undefined;
    const fn = tab === 'pipelines' ? fetchPipelines : fetchMergeRequests;
    const interval = setInterval(fn, 15000);
    return () => clearInterval(interval);
  }, [autoRefresh, tab, fetchPipelines, fetchMergeRequests]);

  /* ─── Pipeline actions ─── */
  const retryPipeline = React.useCallback(
    async (pipelineId: number) => {
      if (!projectId) return;
      setActionLoading(`retry-${pipelineId}`);
      try {
        await gitlabPost(`/projects/${projectId}/pipelines/${pipelineId}/retry`);
        // Invalidate job cache and refresh
        setPipelineJobs(prev => {
          const next = { ...prev };
          delete next[pipelineId];
          return next;
        });
        await fetchPipelines();
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setActionLoading(null);
      }
    },
    [projectId, gitlabPost, fetchPipelines],
  );

  const cancelPipeline = React.useCallback(
    async (pipelineId: number) => {
      if (!projectId) return;
      setActionLoading(`cancel-${pipelineId}`);
      try {
        await gitlabPost(`/projects/${projectId}/pipelines/${pipelineId}/cancel`);
        setPipelineJobs(prev => {
          const next = { ...prev };
          delete next[pipelineId];
          return next;
        });
        await fetchPipelines();
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setActionLoading(null);
      }
    },
    [projectId, gitlabPost, fetchPipelines],
  );

  /* ─── Merge MR ─── */
  const mergeMr = React.useCallback(
    async (mr: MergeRequest) => {
      if (!projectId) return;
      setMerging(true);
      try {
        await gitlabPut(`/projects/${projectId}/merge_requests/${mr.iid}/merge`);
        setMergeDialog(null);
        await fetchMergeRequests();
      } catch (e: unknown) {
        setMrError(e instanceof Error ? e.message : String(e));
      } finally {
        setMerging(false);
      }
    },
    [projectId, gitlabPut, fetchMergeRequests],
  );

  /* ─── Fetch jobs for a pipeline ─── */
  const fetchJobs = React.useCallback(
    async (pipelineId: number) => {
      if (!projectId) return;
      if (pipelineJobs[pipelineId]) return;
      try {
        const data = await gitlabGet(
          `/projects/${projectId}/pipelines/${pipelineId}/jobs?per_page=100`,
        );
        const jobs = (data as Job[]).sort((a, b) => a.id - b.id);
        setPipelineJobs(prev => ({ ...prev, [pipelineId]: jobs }));
      } catch {
        /* ignore */
      }
    },
    [projectId, pipelineJobs, gitlabGet],
  );

  /* ─── Toggle pipeline expand ─── */
  const togglePipeline = React.useCallback(
    (pipelineId: number) => {
      if (expandedPipeline === pipelineId) {
        setExpandedPipeline(null);
        setExpandedJob(null);
      } else {
        setExpandedPipeline(pipelineId);
        setExpandedJob(null);
        fetchJobs(pipelineId);
      }
    },
    [expandedPipeline, fetchJobs],
  );

  /* ─── Group jobs by stage ─── */
  const groupByStage = React.useCallback((jobs: Job[]) => {
    const stages: { name: string; jobs: Job[] }[] = [];
    const stageMap = new Map<string, Job[]>();
    const stageOrder: string[] = [];
    for (const job of jobs) {
      if (!stageMap.has(job.stage)) {
        stageMap.set(job.stage, []);
        stageOrder.push(job.stage);
      }
      stageMap.get(job.stage)!.push(job);
    }
    for (const name of stageOrder) {
      stages.push({ name, jobs: stageMap.get(name)! });
    }
    return stages;
  }, []);

  /* ─── Unique refs for filter ─── */
  const uniqueRefs = React.useMemo(() => {
    const refs = new Set(pipelines.map(p => p.ref));
    return Array.from(refs).sort();
  }, [pipelines]);

  /* ─── Early returns ─── */
  if (!slug) {
    return (
      <EmptyState
        title="CI/CD 정보 없음"
        missing="info"
        description="gitlab.com/project-slug 어노테이션이 필요합니다."
      />
    );
  }

  const isTabLoading = tab === 'pipelines' ? loading : mrLoading;
  const tabError = tab === 'pipelines' ? error : mrError;
  const refreshFn = tab === 'pipelines' ? fetchPipelines : fetchMergeRequests;

  return (
    <Box>
      {/* ─── Tab Switcher + Toolbar ─── */}
      <Box className={classes.toolbar}>
        <ButtonGroup size="small" variant="outlined">
          <Button
            color="primary"
            variant={tab === 'pipelines' ? 'contained' : 'outlined'}
            onClick={() => setTab('pipelines')}
            startIcon={<PlayArrowIcon />}
          >
            Pipelines
          </Button>
          <Button
            color="primary"
            variant={tab === 'merge_requests' ? 'contained' : 'outlined'}
            onClick={() => setTab('merge_requests')}
            startIcon={<MergeTypeIcon />}
          >
            Merge Requests
          </Button>
        </ButtonGroup>

        {tab === 'pipelines' && (
          <>
            <TextField
              select
              label="표시 수"
              value={perPage}
              onChange={e => setPerPage(Number(e.target.value))}
              variant="outlined"
              size="small"
              style={{ minWidth: 90 }}
            >
              {[5, 10, 20, 50].map(v => (
                <MenuItem key={v} value={v}>{v}</MenuItem>
              ))}
            </TextField>

            {uniqueRefs.length > 1 && (
              <TextField
                select
                label="Branch"
                value={refFilter}
                onChange={e => setRefFilter(e.target.value)}
                variant="outlined"
                size="small"
                style={{ minWidth: 120 }}
              >
                <MenuItem value="">전체</MenuItem>
                {uniqueRefs.map(r => (
                  <MenuItem key={r} value={r}>{r}</MenuItem>
                ))}
              </TextField>
            )}
          </>
        )}

        {tab === 'merge_requests' && (
          <TextField
            select
            label="상태"
            value={mrStateFilter}
            onChange={e => setMrStateFilter(e.target.value)}
            variant="outlined"
            size="small"
            style={{ minWidth: 100 }}
          >
            <MenuItem value="opened">Open</MenuItem>
            <MenuItem value="merged">Merged</MenuItem>
            <MenuItem value="closed">Closed</MenuItem>
            <MenuItem value="all">전체</MenuItem>
          </TextField>
        )}

        <FormControlLabel
          control={
            <Switch
              checked={autoRefresh}
              onChange={e => setAutoRefresh(e.target.checked)}
              size="small"
              color="primary"
            />
          }
          label={<Typography variant="caption">Auto (15s)</Typography>}
        />

        <Tooltip title="새로고침">
          <IconButton size="small" onClick={refreshFn} disabled={isTabLoading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>

        {gitlabBaseUrl && slug && (
          <Tooltip title={tab === 'pipelines' ? 'GitLab Pipelines 열기' : 'GitLab MRs 열기'}>
            <IconButton
              size="small"
              onClick={() => {
                const path = tab === 'pipelines' ? '/-/pipelines' : '/-/merge_requests';
                window.open(`${gitlabBaseUrl}/${slug}${path}`, '_blank');
              }}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* ─── Error ─── */}
      {tabError && (
        <Paper style={{ padding: 8, marginBottom: 8, backgroundColor: '#ffebee' }}>
          <Typography color="error" variant="body2">{tabError}</Typography>
        </Paper>
      )}

      {/* ─── Loading ─── */}
      {isTabLoading && (
        <Box display="flex" alignItems="center" justifyContent="center" py={4}>
          <CircularProgress size={24} />
          <Typography variant="body2" style={{ marginLeft: 8 }}>
            {tab === 'pipelines' ? '파이프라인' : 'Merge Request'} 조회 중...
          </Typography>
        </Box>
      )}

      {/* ─── Pipeline Tab ─── */}
      {tab === 'pipelines' && !isTabLoading && (
        <>
          {pipelines.length === 0 && !tabError && (
            <Box py={4} textAlign="center">
              <Typography variant="body2" color="textSecondary">
                파이프라인이 없습니다
              </Typography>
            </Box>
          )}

          <Paper variant="outlined">
            {pipelines.map(pipeline => {
              const isExpanded = expandedPipeline === pipeline.id;
              const jobs = pipelineJobs[pipeline.id];
              const stages = jobs ? groupByStage(jobs) : [];
              const canRetry = ['failed', 'canceled'].includes(pipeline.status);
              const canCancel = ['running', 'pending', 'created'].includes(pipeline.status);

              return (
                <React.Fragment key={pipeline.id}>
                  {/* Pipeline row */}
                  <Box
                    className={classes.pipelineRow}
                    onClick={() => togglePipeline(pipeline.id)}
                    style={isExpanded ? { backgroundColor: 'rgba(25,118,210,0.04)' } : {}}
                  >
                    {statusIcon(pipeline.status, 'inherit')}

                    <Box ml={1} mr={1}>
                      <Typography className={classes.pipelineId}>
                        #{pipelineDisplayId(pipeline)}
                      </Typography>
                    </Box>

                    <Chip
                      label={statusLabel(pipeline.status)}
                      size="small"
                      style={{
                        backgroundColor: statusColor(pipeline.status),
                        color: '#fff',
                        fontWeight: 600,
                        height: 20,
                        marginRight: 8,
                      }}
                    />

                    <Typography className={classes.pipelineRef}>
                      {pipeline.ref}
                    </Typography>

                    <Box flex={1} />

                    {/* Pipeline action buttons */}
                    {canRetry && (
                      <Tooltip title="파이프라인 재실행">
                        <IconButton
                          size="small"
                          onClick={e => { e.stopPropagation(); retryPipeline(pipeline.id); }}
                          disabled={actionLoading === `retry-${pipeline.id}`}
                          style={{ marginRight: 2 }}
                        >
                          {actionLoading === `retry-${pipeline.id}`
                            ? <CircularProgress size={16} />
                            : <ReplayIcon fontSize="small" style={{ color: '#ff9800' }} />}
                        </IconButton>
                      </Tooltip>
                    )}
                    {canCancel && (
                      <Tooltip title="파이프라인 취소">
                        <IconButton
                          size="small"
                          onClick={e => { e.stopPropagation(); cancelPipeline(pipeline.id); }}
                          disabled={actionLoading === `cancel-${pipeline.id}`}
                          style={{ marginRight: 2 }}
                        >
                          {actionLoading === `cancel-${pipeline.id}`
                            ? <CircularProgress size={16} />
                            : <StopIcon fontSize="small" style={{ color: '#757575' }} />}
                        </IconButton>
                      </Tooltip>
                    )}

                    <Typography className={classes.pipelineSha}>
                      {pipeline.sha?.substring(0, 8)}
                    </Typography>

                    <Typography className={classes.pipelineTime}>
                      {formatTime(pipeline.created_at)}
                    </Typography>

                    <IconButton size="small" style={{ marginLeft: 4 }}>
                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>

                  {/* Expanded: jobs panel */}
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Box className={classes.jobsPanel}>
                      {!jobs && (
                        <Box display="flex" alignItems="center" py={1}>
                          <CircularProgress size={16} />
                          <Typography variant="body2" style={{ marginLeft: 8 }}>
                            잡 목록 조회 중...
                          </Typography>
                        </Box>
                      )}

                      {stages.map(stage => (
                        <Box key={stage.name} mb={1}>
                          <Box className={classes.stageRow}>
                            <Typography className={classes.stageLabel}>
                              {stage.name}
                            </Typography>
                            <Box display="flex" flexWrap="wrap">
                              {stage.jobs.map(job => (
                                <Tooltip
                                  key={job.id}
                                  title={`${job.name} — ${statusLabel(job.status)}${job.duration ? ` (${formatDuration(job.duration)})` : ''}${job.allow_failure ? ' (allow_failure)' : ''}`}
                                >
                                  <Chip
                                    className={classes.jobChip}
                                    icon={statusIcon(job.status)}
                                    label={job.name}
                                    size="small"
                                    variant={expandedJob === job.id ? 'default' : 'outlined'}
                                    onClick={e => {
                                      e.stopPropagation();
                                      setExpandedJob(expandedJob === job.id ? null : job.id);
                                    }}
                                    style={
                                      expandedJob === job.id
                                        ? {
                                            backgroundColor: statusColor(job.status),
                                            color: '#fff',
                                          }
                                        : {}
                                    }
                                  />
                                </Tooltip>
                              ))}
                            </Box>
                          </Box>
                        </Box>
                      ))}

                      {/* Selected job log */}
                      {expandedJob && projectId && (() => {
                        const allJobs = stages.flatMap(s => s.jobs);
                        const selectedJob = allJobs.find(j => j.id === expandedJob);
                        if (!selectedJob) return null;
                        return (
                          <JobLogViewer
                            projectId={projectId}
                            jobId={selectedJob.id}
                            jobName={selectedJob.name}
                            jobStatus={selectedJob.status}
                            jobWebUrl={selectedJob.web_url}
                            gitlabGetText={gitlabGetText}
                          />
                        );
                      })()}

                      {/* Pipeline external link */}
                      <Box mt={1} display="flex" justifyContent="flex-end">
                        <Button
                          size="small"
                          startIcon={<OpenInNewIcon />}
                          onClick={() => window.open(pipeline.web_url, '_blank')}
                        >
                          GitLab에서 보기
                        </Button>
                      </Box>
                    </Box>
                  </Collapse>
                </React.Fragment>
              );
            })}
          </Paper>

          {/* ─── Status Bar ─── */}
          <Box className={classes.statusBar}>
            <Typography variant="caption">
              {pipelines.length}개 파이프라인
            </Typography>
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              {autoRefresh && (
                <Chip
                  size="small"
                  icon={<PlayArrowIcon style={{ fontSize: 14 }} />}
                  label="Live"
                  color="primary"
                  style={{ height: 20 }}
                />
              )}
              {loading && <CircularProgress size={12} />}
            </Box>
          </Box>
        </>
      )}

      {/* ─── Merge Requests Tab ─── */}
      {tab === 'merge_requests' && !isTabLoading && (
        <>
          {mergeRequests.length === 0 && !tabError && (
            <Box py={4} textAlign="center">
              <Typography variant="body2" color="textSecondary">
                Merge Request가 없습니다
              </Typography>
            </Box>
          )}

          {mergeRequests.length > 0 && (
            <Paper variant="outlined">
              <Table size="small" className={classes.mrTable}>
                <TableHead>
                  <TableRow>
                    <TableCell>상태</TableCell>
                    <TableCell>제목</TableCell>
                    <TableCell>브랜치</TableCell>
                    <TableCell>작성자</TableCell>
                    <TableCell>업데이트</TableCell>
                    <TableCell align="center">액션</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mergeRequests.map(mr => (
                    <TableRow key={mr.id} hover>
                      <TableCell>
                        <Chip
                          label={mrStateLabel(mr.state)}
                          size="small"
                          style={{
                            backgroundColor: mrStateColor(mr.state),
                            color: '#fff',
                            fontWeight: 600,
                            height: 22,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                          {mr.draft && (
                            <Chip label="Draft" size="small" variant="outlined" style={{ height: 18, fontSize: '0.65rem' }} />
                          )}
                          <Tooltip title={mr.title}>
                            <Typography className={classes.mrTitle}>
                              !{mr.iid} {mr.title}
                            </Typography>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                          <Chip label={mr.source_branch} size="small" variant="outlined" className={classes.branchChip} />
                          <CallSplitIcon style={{ fontSize: 14, color: '#9e9e9e' }} />
                          <Chip label={mr.target_branch} size="small" variant="outlined" className={classes.branchChip} />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" style={{ fontSize: '0.8rem' }}>
                          {mr.author?.name || mr.author?.username}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" style={{ fontSize: '0.75rem', color: '#888' }}>
                          {formatTime(mr.updated_at)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" alignItems="center" justifyContent="center" style={{ gap: 4 }}>
                          {mr.state === 'opened' && !mr.has_conflicts && !mr.draft && (
                            <Tooltip title="Merge 실행">
                              <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                startIcon={<MergeTypeIcon />}
                                onClick={() => setMergeDialog(mr)}
                                style={{ textTransform: 'none', fontSize: '0.75rem' }}
                              >
                                Merge
                              </Button>
                            </Tooltip>
                          )}
                          {mr.has_conflicts && (
                            <Chip label="충돌" size="small" style={{ backgroundColor: '#f44336', color: '#fff', height: 20, fontSize: '0.7rem' }} />
                          )}
                          <Tooltip title="GitLab에서 보기">
                            <IconButton
                              size="small"
                              onClick={() => window.open(mr.web_url, '_blank')}
                            >
                              <OpenInNewIcon style={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}

          {/* ─── MR Status Bar ─── */}
          <Box className={classes.statusBar}>
            <Typography variant="caption">
              {mergeRequests.length}개 Merge Request
            </Typography>
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              {autoRefresh && (
                <Chip
                  size="small"
                  icon={<PlayArrowIcon style={{ fontSize: 14 }} />}
                  label="Live"
                  color="primary"
                  style={{ height: 20 }}
                />
              )}
            </Box>
          </Box>
        </>
      )}

      {/* ─── Merge Confirmation Dialog ─── */}
      <Dialog open={!!mergeDialog} onClose={() => !merging && setMergeDialog(null)}>
        <DialogTitle>Merge Request 머지</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>!{mergeDialog?.iid}</strong> {mergeDialog?.title}
            <br /><br />
            <Chip label={mergeDialog?.source_branch} size="small" variant="outlined" className={classes.branchChip} />
            {' → '}
            <Chip label={mergeDialog?.target_branch} size="small" variant="outlined" className={classes.branchChip} />
            <br /><br />
            이 Merge Request를 머지하시겠습니까?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMergeDialog(null)} disabled={merging}>
            취소
          </Button>
          <Button
            onClick={() => mergeDialog && mergeMr(mergeDialog)}
            color="primary"
            variant="contained"
            disabled={merging}
            startIcon={merging ? <CircularProgress size={16} /> : <MergeTypeIcon />}
          >
            {merging ? '머지 중...' : 'Merge'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
